import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';

import {
  documentList,
  documentSetList,
  documentDelete,
  documentSystemList,
  documentSystemSetList,
  documentSystemDelete,
} from 'lib/api/auth';
import { under2camel, getHeadObj, getHost, converParameters } from 'lib/commonLib';
import { paramConverter } from 'modules/common';
import { showSuccessSnackbar } from 'modules/snackbar';

import NewButton from 'components/molecules/NewButton';
import Dialog from 'components/organisms/Dialog';
import TableBtnWrap from 'components/molecules/TableBtnWrap';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import NewTableContainer from 'components/molecules/NewTable';
import FunctionWrap from 'components/molecules/FunctionWrap';
import Modal from './Modal';
import numeral from 'numeral';
import { setPortal } from 'modules/portal';

const DocumentTable = (props) => {
  const { division } = props;
  const isMain = division === 'main';
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const initModal = {
    open: false,
    type: null,
    params: {},
    data: [],
    headerTitle: '',
  };
  const [modal, setModal] = useState(initModal);
  const initDialog = { open: false, sectionText: '' };
  const [dialog, setDialog] = useState(initDialog);
  const [dataKeyList, setDataKeyList] = useState([]);
  const initParams = isMain
    ? { order: 'REG_DTTM', sort: 'desc' }
    : { order: 'REG_DTTM', sort: 'desc', uri: '/system/systemDocument' };
  const [params, setParams] = useState(initParams);
  const [initialization, setInitialization] = useState(false);
  const [reFetch, setReFetch] = useState(false);

  const [tableListCount, setTableListCount] = useState({
    count: 0,
  });
  const { portal } = useSelector(({ portal }) => ({
    portal: portal
  }));

  useEffect(() => {
    if (initialization) {
      setInitialization(false);
    }
  }, [initialization]);

  useEffect(() => {
    let newKeys = [];
    if (isMain) {
      newKeys = [
        { title: t('고유번호'), name: 'uniqNo' },
        { title: t('파일경로'), name: 'fileSrc' },
        { title: t('파일명'), name: 'fileName' },
        { title: t('제목'), name: 'documentTitle' },
        { title: t('기능명'), name: 'functionName' },
        { title: t('데이터베이스명'), name: 'dbName' },
        { title: t('테이블명'), name: 'tableName' },
        { title: t('작업일'), name: 'workDay', placeholder: t('2024-01-01 or 2024-01-01~2024-01-05') },
        { title: t('기획서 번호'), name: 'planNo' },
        { title: t('기획자'), name: 'planUser' },
        { title: t('개발자명'), name: 'developUser' },
        { title: t('스웨거 주소'), name: 'swagLink' },
        { title: t('GIT 주소'), name: 'gitLink' },
        { title: t('트렐로주소'), name: 'trelloLink' },
      ];
    } else {
      newKeys = [
        { title: t('고유번호'), name: 'uniqNo' },
        { title: t('affiliates'), name: 'affiliates' },
        { title: t('idc'), name: 'idc' },
        { title: t('렉'), name: 'rec' },
        { title: t('용도'), name: 'use' },
        { title: t('VENDER'), name: 'vender' },
        { title: t('모델'), name: 'model' },
        { title: t('구매 혹은 임대'), name: 'property' },
        { title: t('구매 비용 혹은 리스비용(월)'), name: 'cost' },
        { title: t('구입일'), name: 'buyDate' },
        { title: t('구입처'), name: 'buyStore' },
        { title: t('expiryDate'), name: 'expiryDate' },
        { title: t('serialNumber'), name: 'serialNumber' },
        { title: t('hostName'), name: 'hostName' },
        { title: t('oldHostName'), name: 'oldHostName' },
        { title: t('service'), name: 'service' },
        { title: t('mainManager'), name: 'mainManager' },
        { title: t('subManager'), name: 'subManager' },
        { title: t('공인 IP'), name: 'publicIp' },
        { title: t('사설 IP'), name: 'privateIp' },
        { title: t('etcIp'), name: 'etcIp' },
        { title: t('os'), name: 'os' },
        { title: t('app'), name: 'app' },
        { title: t('cpu'), name: 'cpu' },
        { title: t('mem'), name: 'mem' },
        { title: t('disk'), name: 'disk' },
        { title: t('raid'), name: 'raid' },
        { title: t('비고'), name: 'comment' },
      ];
    }
    setDataKeyList(newKeys);
    // eslint-disable-next-line
  }, [division]);

  const addColumnList = (() => {
    const result = new Map();
    result.set(0, {
      id: 'MANAGE_ICONS',
      label: t('관리'),
      align: 'center',
      makeRowFunc: (rowObj) => {
        const { DELETE_BUTTON = '02' } = rowObj;
        return (
          <TableBtnWrap>
            <NewButton
              name="modify"
              title={t('수정')}
              iconBtn
              icon="faEdit"
              onClick={(e) => handleMainModalOpen('modify', rowObj)}
              disableRipple
            />
            {DELETE_BUTTON === '01' && (
              <NewButton
                name="modify"
                title={t('삭제')}
                iconBtn
                icon="faTrashAlt"
                onClick={(e) => handleMainModalOpen('delete', rowObj)}
                disableRipple
              />
            )}
          </TableBtnWrap>
        );
      },
    });
    return result;
  })();

  const customized = {
    // NO: { merged: true },
    UNIQ_NO: {
      makeHead: (headsMap) => {
        let toolTip = '';
        toolTip = (
          <>
            {t('고유번호명')}
            <br /> {'ODA : 운영고도화'}
            <br /> {'SDA : 서비스고도화'}
            <br /> {'ADO : 광고주 플랫폼'}
            <br /> {'SDO : 매체플랫폼'}
            <br /> {'CDP : CDP개발실'}
            <br /> {'TRK : 트래커'}
            <br /> {'WSD : 웹서비스개발'}
            <br /> {'PUB : 퍼블리싱'}
            <br /> {'DEG : 디자인'}
          </>
        );
        return (
          <>
            {isMain ? (
              <InfoTitleArea
                title={getHeadObj(headsMap, 'UNIQ_NO').label}
                tooltipTitle={toolTip}
                iconType="fainfoCircle"
              />
            ) : (
              `${getHeadObj(headsMap, 'UNIQ_NO').label}`
            )}
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const showWindowUrl2 = converParameters({
          url: getHost() + '/admin/auth/documentList.php',
          type: isMain ? 'normal' : 'sysDoc',
          uniqNo: rowObj['UNIQ_NO'],
        });
        return (
            <p
                style={{ cursor: 'pointer' }}
                onClick={(e) => handlePortalUrl(showWindowUrl2)}
            >
              {rowObj['UNIQ_NO']}
            </p>
        );
      },
      orderFlag: true
    },
    SWAG_LINK: {
      makeBody: (rowObj, headsMap) => {
        return (
          <p
            style={{ cursor: 'pointer' }}
            onClick={() => window.open(rowObj['SWAG_LINK'], '_blank')}
          >
            {rowObj['SWAG_LINK']}
          </p>
        );
      },
    },
    TRELLO_LINK: {
      makeBody: (rowObj, headsMap) => {
        return (
            <p
                style={{ cursor: 'pointer' }}
                onClick={() => window.open(rowObj['TRELLO_LINK'], '_blank')}
            >
              {rowObj['TRELLO_LINK']}
            </p>
        );
      },
    },
  };

  const handleMainModalOpen = (type, rowObj) => {
    let newModal = { ...initModal, open: true, type: type };
    let newParams = {};
    if (type === 'add') {
      for (let key in dataKeyList) {
        newParams[dataKeyList[key]['name']] = '';
        // newParams[dataKeyList[key]['name']] = dataKeyList[key]['name'];
      }
      newModal = {
        ...newModal,
        headerTitle: t('등록'),
        cancelBtnTitle: t('등록'),
        params: newParams,
      };
    } else {
      for (let key in rowObj) {
        newParams[under2camel(key)] = rowObj[key];
      }
    }
    if (type === 'delete') {
      setDialog({
        ...initDialog,
        open: true,
        type: type,
        params: newParams,
        sectionText: t('선택한 도큐먼트를 삭제 하시겠습니까?'),
      });
    } else {
      if (type === 'modify') {
        newModal = {
          ...newModal,
          headerTitle: t('수정'),
          cancelBtnTitle: t('수정'),
          params: newParams,
        };
      }
      setModal(newModal);
    }
  };

  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  const getModalParams = (newParams) => {
    if (JSON.stringify(modal.params) !== JSON.stringify(newParams)) {
      setModal({ ...modal, params: newParams });
    }
  };

  const handleSubmit = () => {
    const { type, params } = modal;
    if (type === 'add') {
      const dataReqPromise = isMain ? documentSetList : documentSystemSetList;
      dataReqPromise(paramConverter(params))
        .then((response) => {
          const { message } = response.data;
          setReFetch(true);
          handleClickSnackbar({
            message: message,
            option: { type: 'success' },
          });
          setModal({ ...modal, open: false });
        })
        .catch((error) => {
          handleClickSnackbar({
            message: error,
            option: { type: 'error' },
          });
        })
        .then(() => {
          setReFetch(false);
        });
    } else if (type === 'modify') {
      const dataReqPromise = isMain ? documentSetList : documentSystemSetList;
      dataReqPromise(paramConverter(params))
        .then((response) => {
          const { message } = response.data;
          setReFetch(true);
          handleClickSnackbar({
            message: message,
            option: { type: 'success' },
          });
          setModal({ ...modal, open: false });
        })
        .catch((error) => {
          handleClickSnackbar({
            message: error,
            option: { type: 'error' },
          });
        })
        .then(() => {
          setReFetch(false);
        });
    }
  };

  const handleDialogSubmit = () => {
    const dataReqPromise = isMain ? documentDelete : documentSystemDelete;
    dataReqPromise({ no: dialog['params']['no'] })
      .then((response) => {
        const { message } = response.data;
        handleClickSnackbar({
          message: message,
          option: { type: 'success' },
        });
        setReFetch(true);
      })
      .catch((error) => {
        handleClickSnackbar({ message: error, option: { type: 'error' } });
      })
      .then(() => {
        setDialog({ ...dialog, open: false });
        setReFetch(false);
      });
  };

  let tableTitle = '';
  if (tableListCount.count !== 'undefined') {
    tableTitle =
      t('총 게시글') + ' : ' + numeral(tableListCount.count).format();
  }

  const handleClickSnackbar = useCallback((snackbarOptions) => {
    dispatch(showSuccessSnackbar(snackbarOptions));
    //eslint-disable-next-line
  }, []);

  const handlePortalUrl = (url) => {
    let newPortal = portal.portal;
    let popIndex = newPortal.length;
    newPortal[popIndex] = { url: url, cnt: popIndex };
    dispatch(setPortal(newPortal));
  };

  const showWindowUrl = converParameters({
    url: getHost() + '/admin/auth/statusBoard.php',
    type: isMain ? 'normal' : 'sysDoc',
  });

  return (
    <>
      <FunctionWrap loading={false}>
        <NewButton onClick={(e) => handleMainModalOpen('add')}>
          {isMain ? t('도큐먼트 신규 등록') : t('시스템 도큐먼트 신규 등록')}
        </NewButton>
        <NewButton onClick={(e) => handlePortalUrl(showWindowUrl)}>
          {isMain ? t('연도별 현황판') : t('연도별 현황판')}
        </NewButton>
      </FunctionWrap>
      <NewTableContainer
        title={tableTitle}
        params={params}
        dataReqPromise={isMain ? documentList : documentSystemList}
        getParam={getParam}
        useTotal={false}
        reFetch={reFetch}
        addColumnList={addColumnList}
        customized={customized}
        useExcel={isMain ? false : true}
        dataCallBack={(newCnt) => {
          setTableListCount(newCnt);
        }}
      />
      {modal['open'] && (
        <Modal
          dataKeyList={dataKeyList}
          modal={modal}
          getParam={getModalParams}
          handleSubmit={handleSubmit}
          handleModalClose={(f) => setModal(initModal)}
        />
      )}
      <Dialog
        dialog={dialog}
        handleClose={() => setDialog({ ...dialog, open: false })}
        handleSubmit={handleDialogSubmit}
      />
    </>
  );
};

export default DocumentTable;
